import { Link, useLocation } from "react-router-dom";

/**
 * SiteNavbar — shared translucent capsule nav used by every internal
 * route page (/services, /about, /contacts). Same visual language as the
 * home Navbar (translucent bg, backdrop-blur, tracked-uppercase labels,
 * dark-fill active state) but wired to router paths.
 *
 * Home links use the "/#..." hash form so they scroll to the right home
 * section after routing back.
 */

type NavItem = { label: string; to: string };

const NAV_ITEMS: NavItem[] = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About Us", to: "/about" },
  { label: "Contacts", to: "/contacts" },
];

export default function SiteNavbar() {
  const location = useLocation();

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/" && !location.hash;
    return location.pathname === to;
  };

  return (
    <header className="absolute top-0 left-0 right-0 z-50 pt-6 md:pt-8 px-6 md:px-10">
      <div className="flex items-center justify-between w-full max-w-[1600px] mx-auto">
        {/* Brand mark */}
        <Link
          to="/"
          aria-label="Serene home"
          className="group relative flex items-center justify-center w-11 h-11 rounded-full border border-foreground-950/25 bg-background-50/40 backdrop-blur-md cursor-pointer transition-colors duration-300 hover:bg-background-50/70"
        >
          <span className="font-heading text-[11px] tracking-[0.14em] font-medium text-foreground-950">
            SM
          </span>
        </Link>

        {/* Center pill nav */}
        <nav className="hidden md:block">
          <ul className="flex items-center gap-1 rounded-full border border-foreground-950/20 bg-background-50/40 backdrop-blur-md px-1.5 py-1.5">
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.to);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`relative flex items-center justify-center px-5 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase font-medium whitespace-nowrap cursor-pointer transition-colors duration-300 ${
                      active
                        ? "bg-foreground-950 text-background-50"
                        : "text-foreground-950/80 hover:text-foreground-950"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right side — social */}
        <div className="flex items-center gap-2">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label="Instagram"
            className="w-11 h-11 flex items-center justify-center rounded-full border border-foreground-950/25 bg-background-50/40 backdrop-blur-md text-foreground-950 cursor-pointer transition-colors duration-300 hover:bg-background-50/70"
          >
            <i className="ri-instagram-line text-base"></i>
          </a>
        </div>
      </div>
    </header>
  );
}